// App.js - UPDATED with new features
import React, { useState, useEffect, useRef } from 'react';
import { Send, Home, BarChart3, MapPin, DollarSign, Calendar, AlertCircle, Loader2, CheckCircle, Camera, Calculator, FileText, Paperclip, X, TrendingUp } from 'lucide-react';
import apiService from './services/apiService';
import { supabase } from './lib/supabase';
import { filterComparables } from './utils/filterUtils';
import { resortComparablesBySubject, isRadiusChanged } from './utils/similarityUtils';
import { addAddressToHistory } from './utils/addressHistoryUtils';

// Import existing components
import PropertyImageGallery from './components/PropertyImageGallery';
import PropertyDetailsModal from './components/PropertyDetailsModal';
import ComparablesTable from './components/ComparablesTable';
import PropertyAnalysis from './components/PropertyAnalysis';
import PropertyComparisonTable from './components/PropertyComparisonTable';
import PropertyFilters from './components/PropertyFilters';

// Import NEW components
import ManualAdjustments from './components/ManualAdjustments';
import AINarrativeGenerator from './components/AINarrativeGenerator';
import EditablePropertyFields from './components/EditablePropertyFields';
import ComparableSearchFilters from './components/ComparableSearchFilters';
import ExpandableChatMessage from './components/ExpandableChatMessage';
import SimpleChatMessage from './components/SimpleChatMessage';
import ChatTable from './components/ChatTable';
import SmartAdjustments from './components/SmartAdjustments';
import UAD36ReportGenerator from './components/UAD36ReportGenerator';
import URARForm from './components/URARForm';
import UADReportView from "./components/UADReportView";
import BracketingDashboard from './components/BracketingDashboard';
import AddressInputWithHistory from './components/AddressInputWithHistory';
import AddressAutocomplete from './components/AddressAutocomplete';

const PropertyAI = () => {
  // Existing state
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: 'Welcome to Property AI. Enter an address to get started with your appraisal analysis.'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [addressInput, setAddressInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentAddress, setCurrentAddress] = useState('');
  const [marketData, setMarketData] = useState(null);
  const [comparableData, setComparableData] = useState([]);
  const [filteredComparableData, setFilteredComparableData] = useState([]);
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [subjectProperty, setSubjectProperty] = useState(null);
  const [currentView, setCurrentView] = useState('chat');
  const [debugData, setDebugData] = useState(null);

  // NEW state for enhanced features
  const [adjustedComparables, setAdjustedComparables] = useState([]);
  const [generatedNarrative, setGeneratedNarrative] = useState('');
  const [editedSubjectProperty, setEditedSubjectProperty] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  
  // NEW: Selected comparables for side-by-side comparison
  const [selectedComparables, setSelectedComparables] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // NEW: Image upload state for chat
  const [selectedImages, setSelectedImages] = useState([]);

  // AI COMPARABLE SELECTION (Milestone 1)
  const [isAISelecting, setIsAISelecting] = useState(false);
  const [aiSelectionResult, setAISelectionResult] = useState(null);
  const [aiProgress, setAIProgress] = useState(null); // Real-time progress tracking
  const [selectedForAI, setSelectedForAI] = useState([]); // Comparables selected for AI analysis
  const [aiAbortController, setAiAbortController] = useState(null); // ✅ FIX ISSUE #3: Abort controller for Stop button

  // BRACKETING ANALYSIS (NEW)
  const [bracketingData, setBracketingData] = useState(null);
  const [isBracketAnalyzing, setIsBracketAnalyzing] = useState(false);

  // State to track current filters
  const [currentFilters, setCurrentFilters] = useState({});

  // 🆕 NEW: State for smart comparable caching and re-sorting
  const [allComparables, setAllComparables] = useState([]); // ALL comparables from last API call
  const [lastFetchedRadius, setLastFetchedRadius] = useState(null); // Track last radius used for API
  const [totalAvailable, setTotalAvailable] = useState(0); // Total available from API
  const [displayLimit, setDisplayLimit] = useState(20); // Current display limit
  const [showAllComparables, setShowAllComparables] = useState(false); // Toggle to show all

  // Refs for auto-scrolling
  const leftChatRef = useRef(null);
  const mainChatRef = useRef(null);

  // Auto-scroll function
  const scrollToBottom = () => {
    // Scroll left sidebar chat
    if (leftChatRef.current) {
      leftChatRef.current.scrollTop = leftChatRef.current.scrollHeight;
    }
    
    // Scroll main chat area (only if on chat tab)
    if (mainChatRef.current && currentView === 'chat') {
      mainChatRef.current.scrollTop = mainChatRef.current.scrollHeight;
    }
  };

  // Auto-scroll when messages change or loading state changes
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 100); // Small delay to ensure DOM is updated

    return () => clearTimeout(timer);
  }, [messages, isLoading, currentView]);

  // ✅ FIX ISSUE #4: Reapply filters when comparableData changes
  useEffect(() => {
    console.log('🔄 Comparables data changed, reapplying filters...');
    console.log('📊 New comparableData length:', comparableData.length);
    console.log('🔍 Current filters:', currentFilters);

    if (currentFilters && Object.keys(currentFilters).length > 0 && Object.values(currentFilters).some(v => v !== '' && v !== null && v !== undefined)) {
      const filtered = filterComparables(comparableData, currentFilters);
      console.log('✅ Refiltered to', filtered.length, 'comparables');
      setFilteredComparableData(filtered);
    } else {
      console.log('ℹ️ No active filters, showing all comparables');
      setFilteredComparableData(comparableData);
    }
  }, [comparableData, currentFilters]);

  // Handle comparable selection for side-by-side comparison
  const handleComparableSelection = (comparable, isSelected) => {
    if (isSelected) {
      setSelectedComparables(prev => [...prev, comparable]);
    } else {
      setSelectedComparables(prev => prev.filter(comp =>
        (comp.mlsNumber || comp.address) !== (comparable.mlsNumber || comparable.address)
      ));
    }
  };

  // Handle AI analysis selection (checkboxes for which comparables to analyze)
  const handleAISelectionChange = (comparable, index, isSelected) => {
    if (isSelected) {
      setSelectedForAI(prev => [...prev, { comparable, index }]);
    } else {
      setSelectedForAI(prev => prev.filter(item => item.index !== index));
    }
  };

  // Navigation items - UPDATED with new tabs
  const navigationItems = [
    { id: 'chat', label: 'Chat', icon: Send },
    { id: 'market-summary', label: 'Market Summary', icon: BarChart3 },
    { id: 'property-edit', label: 'Edit Property', icon: Home }, // NEW
    { id: 'comparables', label: 'Find Comparables', icon: MapPin }, // UPDATED
    { id: 'comparison', label: 'Comparison', icon: DollarSign },
    { id: 'adjustments', label: 'Adjustments', icon: Calculator }, // UPDATED
    { id: 'smart-adjustments', label: 'Smart Adjustments', icon: TrendingUp }, // NEW
    { id: 'narrative', label: 'AI Narrative', icon: FileText }, // NEW
  ];

  // Handle filter changes (existing functionality)
  const handleFilterChange = (filters) => {
    console.log('🔍 FILTER CHANGE:', filters);
    console.log('📊 Filtering from', comparableData.length, 'comparables');

    // ✅ FIX ISSUE #4: Save filters to state so they persist when data changes
    setCurrentFilters(filters);

    const filtered = filterComparables(comparableData, filters);
    console.log('✅ Filtered to', filtered.length, 'comparables');
    setFilteredComparableData(filtered);

    // ✅ FIX: Provide user feedback when filters reduce to 0 results
    if (filtered.length === 0 && comparableData.length > 0) {
      const hasActiveFilters = Object.values(filters).some(v => v !== '' && v !== null && v !== undefined && v !== false);
      if (hasActiveFilters) {
        console.warn('⚠️ All properties filtered out - notifying user');
        // Don't add a message every time, just log for now
        // The UI will show "No results" which is sufficient
      }
    }
  };

  // Handle image upload
  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length > 0) {
      const newImages = imageFiles.map(file => ({
        id: Date.now() + Math.random(),
        file,
        url: URL.createObjectURL(file),
        name: file.name
      }));

      setSelectedImages(prev => [...prev, ...newImages]);

      // Add confirmation message to chat
      const confirmationMessage = {
        id: messages.length + 1,
        type: 'ai',
        content: `✅ ${imageFiles.length} photo${imageFiles.length > 1 ? 's' : ''} uploaded successfully (${imageFiles.map(f => f.name).join(', ')}). These photos will be analyzed when you send your next message.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, confirmationMessage]);
    }
  };

  // Remove selected image
  const removeImage = (imageId) => {
    setSelectedImages(prev => {
      const imageToRemove = prev.find(img => img.id === imageId);
      if (imageToRemove) {
        URL.revokeObjectURL(imageToRemove.url);
      }
      return prev.filter(img => img.id !== imageId);
    });
  };

  // Store parsed address components from Geoapify (bypasses parseAddress on backend)
  const [parsedAddressComponents, setParsedAddressComponents] = useState(null);

  // Called when user picks a Geoapify suggestion — has pre-parsed components
  const handleAddressSuggestionSelect = (suggestion) => {
    setAddressInput(suggestion.full);
    setParsedAddressComponents({
      street: suggestion.street,
      city: suggestion.city,
      state: suggestion.state,
      zip: suggestion.zip,
    });
  };

  // Handle address submit from dedicated address input
  const handleAddressInputSubmit = () => {
    if (!addressInput.trim()) return;
    // If we have pre-parsed components from Geoapify, pass structured address
    // Otherwise fall back to string parsing
    if (parsedAddressComponents && parsedAddressComponents.street) {
      const structured = `${parsedAddressComponents.street}, ${parsedAddressComponents.city}, ${parsedAddressComponents.state} ${parsedAddressComponents.zip}`;
      handleAddressSearch(structured);
    } else {
      handleAddressSearch(addressInput.trim());
    }
    setAddressInput('');
    setParsedAddressComponents(null);
  };

  // Handle address search (existing with minor updates)
  const handleAddressSearch = async (address) => {
    setIsLoading(true);
    setError(null);

    // ✅ FIX: Reset filters and selected comparables when searching new address
    setCurrentFilters({});
    setSelectedComparables([]);
    setSelectedForAI([]);
    setAISelectionResult(null);
    setBracketingData(null);

    // 🆕 Reset smart caching state for new address
    setAllComparables([]);
    setLastFetchedRadius(null);
    setTotalAvailable(0);
    setShowAllComparables(false);

    try {
      // 🆕 STEP 1: Normalize address using OpenAI
      console.log('🏠 Normalizing address with OpenAI:', address);
      const normalizedResult = await apiService.normalizeAddress(address);

      let finalAddress = address; // Default to original

      if (normalizedResult.success && normalizedResult.isValid) {
        finalAddress = normalizedResult.normalized;
        console.log('✅ Address normalized:', finalAddress);
      } else {
        console.warn('⚠️ Normalization failed, using original address:', address);
      }

      setCurrentAddress(finalAddress);

      // 🆕 STEP 2: Validate normalized address
      const validation = await apiService.validateAddress(finalAddress);

      if (!validation.isValid) {
        throw new Error(validation.error || 'Invalid address format');
      }

      // Use larger default radius (3 miles) for initial search to ensure we get comparables
      const defaultSearchRadius = 3;
      const defaultDisplayLimit = 20;
      const [marketResponse, comparablesResponse, propertyResponse] = await Promise.allSettled([
        apiService.getMarketSummary(address),
        apiService.getComparables(address, defaultSearchRadius, defaultDisplayLimit, true), // returnAll=true for caching
        apiService.getPropertyDetails(address)
      ]);

      console.log('🔍 === FRONTEND API RESPONSES DEBUG ===');
      console.log('📈 Market Response Status:', marketResponse.status);
      if (marketResponse.status === 'fulfilled') {
        console.log('📈 Market Response Data:', marketResponse.value);
        console.log('📈 Market Response Debug Info:', marketResponse.value.data?.debug);
      } else {
        console.error('📈 Market Response Error:', marketResponse.reason?.message || marketResponse.reason);
      }


      console.log('🏘️ Comparables Response Status:', comparablesResponse.status);
      if (comparablesResponse.status === 'fulfilled') {
        console.log('🏘️ Comparables Response Data:', comparablesResponse.value);
        console.log('🏘️ Comparables Response Debug Info:', comparablesResponse.value.data?.debug);
      } else {
        console.error('🏘️ Comparables Response Error:', comparablesResponse.reason?.message || comparablesResponse.reason);
      }

      console.log('🏠 Property Details Response Status:', propertyResponse.status);
      if (propertyResponse.status === 'fulfilled') {
        console.log('🏠 Property Details Response Data:', propertyResponse.value);
      } else {
        console.error('🏠 Property Details Response Error:', propertyResponse.reason?.message || propertyResponse.reason);
      }

      console.log('🔍 === END FRONTEND DEBUG ===');

      setDebugData({
        marketResponse: marketResponse.status === 'fulfilled' ? marketResponse.value : null,
        comparablesResponse: comparablesResponse.status === 'fulfilled' ? comparablesResponse.value : null,
        propertyResponse: propertyResponse.status === 'fulfilled' ? propertyResponse.value : null
      });

      // Collect all errors to show user
      const errors = [];

      // Process market data
      if (marketResponse.status === 'fulfilled') {
        console.log('📈 Setting market data:', marketResponse.value.data);
        setMarketData(marketResponse.value.data);
      } else {
        console.warn('Market data failed:', marketResponse.reason);
        errors.push('Market data unavailable');
        setMarketData(null);
      }

      // Process comparables data
      if (comparablesResponse.status === 'fulfilled') {
        console.log('🏘️ Setting comparables data');
        const responseData = comparablesResponse.value.data;
        const comps = responseData.comparables || [];
        const apiTotalAvailable = responseData.totalAvailable || comps.length;

        // 🆕 Store ALL comparables for smart re-sorting
        setAllComparables(comps);
        setTotalAvailable(apiTotalAvailable);
        setLastFetchedRadius(defaultSearchRadius);
        setDisplayLimit(defaultDisplayLimit);

        // Display limited set initially
        const displayedComps = comps.slice(0, defaultDisplayLimit);
        setComparableData(displayedComps);
        setFilteredComparableData(displayedComps);

        console.log(`📊 Cached ${comps.length} comparables, showing ${displayedComps.length}, total available: ${apiTotalAvailable}`);

        // Use Subject.Listing if it has data, else fall back to Subject.PublicRecord
        // Some properties have no active MLS listing so Listing is empty {}
        const rawListing = responseData.Subject?.Listing;
        const rawPublicRecord = responseData.Subject?.PublicRecord;
        const listingHasData = rawListing && Object.keys(rawListing).length > 0;
        const publicRecordHasData = rawPublicRecord && Object.keys(rawPublicRecord).length > 0;

        let subjectProp = null;
        // Prefer rawData from the transformed subject — it already has Listing + PublicRecord +
        // extra ListingDetails fields (Sewer, LaundryFeatures, CountyOrParish, Utilities, PetsAllowed)
        if (responseData.subjectProperty?.rawData) {
          subjectProp = responseData.subjectProperty.rawData;
          console.log('🏠 Using subjectProperty.rawData (fully merged)');
        } else if (listingHasData) {
          subjectProp = { ...rawPublicRecord, ...rawListing };
          console.log('🏠 Using Subject.Listing merged with PublicRecord');
        } else if (publicRecordHasData) {
          console.log('🏠 Subject.Listing empty — using Subject.PublicRecord as subject property');
          subjectProp = rawPublicRecord;
        }

        if (subjectProp) {
          console.log('🏠 Subject property set:', subjectProp.UnparsedAddress || subjectProp.StreetAddress || 'address unknown');
          setSubjectProperty(subjectProp);
        } else if (comps.length > 0) {
          // Fallback to first comparable if subject property not available
          console.log('🏠 FALLBACK: Using first comparable as subject property');
          const firstComparable = comps[0];
          setSubjectProperty(firstComparable.rawData || firstComparable);
        }
      } else {
        console.warn('Comparables data failed:', comparablesResponse.reason);
        errors.push('Comparable properties unavailable');
        setComparableData([]);
        setFilteredComparableData([]);
        setAllComparables([]);
        setTotalAvailable(0);
      }

      // Process property details
      if (propertyResponse.status === 'fulfilled') {
        console.log('🏠 Setting property details:', propertyResponse.value.data);
        setPropertyDetails(propertyResponse.value.data);
      } else {
        console.warn('Property details failed:', propertyResponse.reason);
        errors.push('Property details unavailable');
        setPropertyDetails(null);
      }

      // If all requests failed, throw error
      if (errors.length === 3) {
        throw new Error('Unable to load property data. Please try again or check the address.');
      }

      const successMessage = {
        id: messages.length + 2,
        type: 'ai',
        content: `I've successfully analyzed the property at ${finalAddress}. Found ${comparablesResponse.status === 'fulfilled' ? comparablesResponse.value.data.comparables?.length || 0 : 0} comparable properties and market data. Check the Market Summary and Comparables tabs for detailed analysis.`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, successMessage]);

      // 🆕 STEP 3: Save to history ONLY after successful search
      addAddressToHistory(finalAddress);
      console.log('✅ Address saved to history:', finalAddress);

      // Auto-redirect to Market Summary after successful address search
      setCurrentView('market-summary');

    } catch (error) {
      console.error('Address search error:', error);
      setError(error.message);
      
      const errorMessage = {
        id: messages.length + 2,
        type: 'ai',
        content: `I encountered an issue analyzing ${address}: ${error.message}. Please try a different address or check the format.`,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle send message with AI processing
  const handleSendMessage = async () => {
    if (!inputMessage.trim() && selectedImages.length === 0) return;

    // Convert images to base64 for API
    let imageData = null;
    if (selectedImages.length > 0) {
      try {
        imageData = await Promise.allSettled(
          selectedImages.map(async (image) => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = () => reject(new Error(`Failed to read ${image.name}`));
              reader.readAsDataURL(image.file);
            });
          })
        );
        // Filter out failed images
        imageData = imageData
          .filter(result => result.status === 'fulfilled')
          .map(result => result.value);

        if (imageData.length === 0) {
          console.warn('All image uploads failed');
          imageData = null;
        }
      } catch (error) {
        console.error('Error processing images:', error);
        imageData = null;
      }
    }
    
    const newMessage = {
      id: messages.length + 1,
      type: 'user',
      content: inputMessage || (selectedImages.length > 0 ? `Uploaded ${selectedImages.length} image(s)` : ''),
      timestamp: new Date(),
      images: selectedImages.length > 0 ? [...selectedImages] : undefined
    };
    
    setMessages([...messages, newMessage]);
    setIsLoading(true);
    
    try {
      // Create context from current property data
      const context = {
        currentProperty: subjectProperty,
        comparables: comparableData?.slice(0, 5) || [], // Include top 5 for context
        currentAddress: currentAddress
      };

      // Process query with enhanced AI (including images)
      const aiResult = await apiService.processQuery(inputMessage.trim(), context, imageData);
      console.log('🧠 Enhanced AI result:', aiResult);
      
      if (aiResult.success) {
        // Add AI response message with enhanced data
        const aiMessage = {
          id: messages.length + 2,
          type: 'ai',
          content: aiResult.response,
          timestamp: new Date(),
          action: aiResult.action,
          confidence: aiResult.confidence,
          webSearchResults: aiResult.webSearchResults,
          tableData: aiResult.tableData,
          calculationResult: aiResult.calculationResult,
          imageAnalysis: aiResult.imageAnalysis,
          shouldTriggerPropertySearch: aiResult.shouldTriggerPropertySearch,
          address: aiResult.address
        };
        setMessages(prev => [...prev, aiMessage]);
        
        // Handle different action types
        switch (aiResult.action) {
          case 'PROPERTY_ANALYSIS':
            if (aiResult.address) {
              console.log('🏠 Triggering property search for:', aiResult.address);
              
              // Check if this is a specific address or area search
              const isSpecificAddress = /^\d+\s/.test(aiResult.address.trim()); // Starts with number
              
              if (isSpecificAddress && aiResult.shouldTriggerPropertySearch) {
                // Specific address - redirect to Market Summary
                await handleAddressSearch(aiResult.address);
              } else {
                // Area search - show property list in chat
                await handleAreaPropertySearch(aiResult.address, inputMessage.trim());
              }
            }
            break;
            
          case 'WEB_SEARCH':
            console.log('🌐 Web search completed');
            // Response already includes web search results
            break;
            
          case 'GENERAL_QUESTION':
            console.log('💡 General question answered');
            // Response already includes expert answer
            break;
            
          case 'APP_HELP':
            console.log('🆘 App help provided');
            // Response already includes help information
            break;
            
          default:
            console.log('🤖 Default AI response provided');
        }
        
      } else if (apiService.isValidAddress(inputMessage)) {
        // Fallback: direct address validation if AI fails
        console.log('📍 AI failed, trying direct address validation');
        await handleAddressSearch(inputMessage.trim());
      } else {
        // Complete fallback
        const fallbackResponse = {
          id: messages.length + 2,
          type: 'ai',
          content: "I can help you analyze properties when you provide an address, or answer questions about real estate. What would you like to know?",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, fallbackResponse]);
      }
    } catch (error) {
      console.error('Message processing error:', error);
      
      // Fallback to basic address check if AI fails
      if (apiService.isValidAddress(inputMessage)) {
        await handleAddressSearch(inputMessage.trim());
      } else {
        const errorResponse = {
          id: messages.length + 2,
          type: 'ai',
          content: "I can help you analyze properties when you provide an address. Please enter a complete address like '123 Main St, Dallas, TX 75001'.",
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, errorResponse]);
      }
    } finally {
      setIsLoading(false);
    }
    
    setInputMessage('');
    // Clear selected images after sending
    selectedImages.forEach(img => URL.revokeObjectURL(img.url));
    setSelectedImages([]);
  };


  // NEW: Handle adjustments change
  const handleAdjustmentsChange = (adjusted) => {
    setAdjustedComparables(adjusted);
    console.log('🔧 Adjustments applied:', adjusted);
  };

  // NEW: Handle property update from EditablePropertyFields
  const handlePropertyUpdate = async (updatedPropertyData) => {
    try {
      console.log('🏠 Updating subject property:', updatedPropertyData);
      
      // Call API to update property
      const result = await apiService.updateSubjectProperty(updatedPropertyData);
      
      if (result.success) {
        // Update local state
        setEditedSubjectProperty(result.data);
        
        // Also update the main subject property for comparison
        setSubjectProperty(prev => ({
          ...prev,
          ...result.data
        }));
        
        console.log('✅ Subject property updated successfully');
        return result;
      } else {
        throw new Error(result.message || 'Failed to update property');
      }
    } catch (error) {
      console.error('❌ Property update error:', error);
      throw error;
    }
  };

  // 🆕 UPDATED: Smart comparable search - only calls API when radius changes
  // Re-sorts existing data locally when only subject property details change
  const handleSubjectBasedSearch = async (subjectPropertyData, searchCriteria) => {
    const radius = searchCriteria?.searchRadius || searchCriteria?.radius || 3;
    const maxResults = searchCriteria?.maxResults || 20;
    const address = currentAddress;

    if (!address) {
      console.warn('❌ No address set for comparable search');
      const errorMessage = {
        id: messages.length + 1,
        type: 'ai',
        content: '❌ Error: No property address is set. Please search for a property first.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      return { success: false, message: 'No address set' };
    }

    console.log('🔍 Smart search check:', {
      requestedRadius: radius,
      lastFetchedRadius,
      cachedComparables: allComparables.length,
      totalAvailable,
      maxResults
    });

    // 🎯 SMART CHECK: Do we need a new API call?
    const radiusChanged = isRadiusChanged(radius, lastFetchedRadius);
    const needsMoreData = maxResults > allComparables.length && maxResults > totalAvailable;
    const hasNoData = allComparables.length === 0;

    if (radiusChanged || hasNoData) {
      // 🔄 RADIUS CHANGED OR NO DATA - Make new API call
      console.log('🌐 Making API call - radius changed or no cached data');
      setIsSearching(true);

      const searchingMessage = {
        id: messages.length + 1,
        type: 'ai',
        content: `🔍 Fetching comparables within ${radius} mile(s)...`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, searchingMessage]);

      try {
        // Request ALL comparables from API (returnAll=true)
        const comparablesResponse = await apiService.getComparables(address, radius, maxResults, true);
        const fetchedComparables = comparablesResponse?.data?.comparables || [];
        const apiTotalAvailable = comparablesResponse?.data?.totalAvailable || fetchedComparables.length;

        console.log(`📊 API returned ${fetchedComparables.length} comparables, total available: ${apiTotalAvailable}`);

        if (fetchedComparables.length === 0) {
          setAllComparables([]);
          setComparableData([]);
          setFilteredComparableData([]);
          setTotalAvailable(0);
          setLastFetchedRadius(radius);
          setIsSearching(false);

          const noResultsMessage = {
            id: messages.length + 2,
            type: 'ai',
            content: `⚠️ No comparable properties found within ${radius} mile(s). Try increasing the search radius.`,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, noResultsMessage]);

          return { success: true, comparables: [], total: 0 };
        }

        // Store ALL comparables for future re-sorting
        setAllComparables(fetchedComparables);
        setTotalAvailable(apiTotalAvailable);
        setLastFetchedRadius(radius);
        setDisplayLimit(maxResults);

        // Re-sort by similarity to subject property
        const sortedComparables = resortComparablesBySubject(fetchedComparables, subjectPropertyData, maxResults);

        setComparableData(sortedComparables);
        setFilteredComparableData(sortedComparables);
        setIsSearching(false);

        // Success message
        const successMessage = {
          id: messages.length + 2,
          type: 'ai',
          content: `✅ Found ${apiTotalAvailable} comparables within ${radius} mi. Showing top ${sortedComparables.length} most similar to your subject property.`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, successMessage]);

        return {
          success: true,
          comparables: sortedComparables,
          total: sortedComparables.length,
          totalAvailable: apiTotalAvailable
        };

      } catch (error) {
        console.error('❌ API call error:', error);
        setIsSearching(false);
        throw error;
      }

    } else {
      // ✅ RADIUS SAME - Re-sort existing data locally (NO API CALL)
      console.log('⚡ Re-sorting locally - radius unchanged, using cached data');

      const resortingMessage = {
        id: messages.length + 1,
        type: 'ai',
        content: `⚡ Re-sorting ${allComparables.length} comparables based on your updated criteria...`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, resortingMessage]);

      // Re-sort using utility function
      const sortedComparables = resortComparablesBySubject(allComparables, subjectPropertyData, maxResults);

      setComparableData(sortedComparables);
      setFilteredComparableData(sortedComparables);
      setDisplayLimit(maxResults);

      // Success message
      const successMessage = {
        id: messages.length + 2,
        type: 'ai',
        content: `✅ Re-sorted comparables. Showing top ${sortedComparables.length} of ${totalAvailable} available (within ${lastFetchedRadius} mi radius).`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, successMessage]);

      return {
        success: true,
        comparables: sortedComparables,
        total: sortedComparables.length,
        totalAvailable: totalAvailable,
        resortedLocally: true
      };
    }
  };

  // 🆕 NEW: Toggle showing all comparables vs limited
  const handleShowAllToggle = () => {
    if (!allComparables?.length) return;
    if (showAllComparables) {
      // Switch back to limited view
      const limited = allComparables.slice(0, displayLimit);
      setComparableData(limited);
      setFilteredComparableData(limited);
      setShowAllComparables(false);
    } else {
      // Show all
      setComparableData(allComparables);
      setFilteredComparableData(allComparables);
      setShowAllComparables(true);
    }
  };

  // NEW: Handle text-based comparable search
  const handleTextBasedSearch = async (location, searchText, filters, searchCriteria) => {
    setIsSearching(true);
    try {
      console.log('🔍 Searching comparables by text:', { location, searchText, filters });
      
      const result = await apiService.findComparablesByText(location, searchText, filters, searchCriteria);
      
      if (result.success) {
        setSearchResults(result);
        setComparableData(result.comparables || []);
        setFilteredComparableData(result.comparables || []);
        
        console.log(`✅ Found ${result.total} comparable properties`);
        return result;
      } else {
        throw new Error(result.message || 'Failed to find comparable properties');
      }
    } catch (error) {
      console.error('❌ Text-based search error:', error);
      throw error;
    } finally {
      setIsSearching(false);
    }
  };

  // NEW: Handle area property search (for chat display)
  const handleAreaPropertySearch = async (area, originalQuery) => {
    try {
      console.log('🏘️ Searching for properties in area:', area);
      
      // Call area search API
      const response = await apiService.searchPropertiesByArea(area, originalQuery);
      
      if (response.success && response.properties && response.properties.length > 0) {
        // Create property list message
        const propertyListMessage = {
          id: messages.length + 3,
          type: 'ai',
          content: `Found ${response.properties.length} properties matching your criteria:`,
          timestamp: new Date(),
          propertyList: response.properties,
          searchArea: area,
          originalQuery: originalQuery
        };
        
        setMessages(prev => [...prev, propertyListMessage]);
      } else {
        // No properties found message
        const noResultsMessage = {
          id: messages.length + 3,
          type: 'ai',
          content: `I couldn't find any properties matching your criteria in ${area}. Try adjusting your search parameters or check a different area.`,
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, noResultsMessage]);
      }
      
    } catch (error) {
      console.error('Area property search error:', error);
      
      const errorMessage = {
        id: messages.length + 3,
        type: 'ai',
        content: `I encountered an issue searching for properties in ${area}. Please try again or refine your search.`,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  // AI COMPARABLE SELECTION (Milestone 1) - WITH REAL-TIME STREAMING
  const handleAISmartSelect = async () => {
    try {
      // ✅ FIX ISSUE #3: Create abort controller for Stop button
      const controller = new AbortController();
      setAiAbortController(controller);

      setIsAISelecting(true);
      setError(null);
      setAIProgress({ type: 'start', progress: 0, message: 'Starting AI analysis...' });

      console.log('🤖 Starting AI Smart Select with STREAMING...');

      // Validation
      if (!subjectProperty) {
        setError('Please select a subject property first');
        setIsAISelecting(false);
        setAIProgress(null);
        return;
      }

      if (!comparableData || comparableData.length === 0) {
        setError('No comparables available. Please search for comparables first.');
        setIsAISelecting(false);
        setAIProgress(null);
        return;
      }

      // Get comparables to analyze - only selected ones, or all if none selected
      let comparablesToAnalyze = comparableData;
      let indexMapping = comparableData.map((_, i) => i); // Maps result index to original index

      if (selectedForAI.length > 0) {
        // Only analyze selected comparables
        comparablesToAnalyze = selectedForAI.map(item => comparableData[item.index]);
        indexMapping = selectedForAI.map(item => item.index);
        console.log(`🎯 Analyzing ${selectedForAI.length} selected comparables (not all ${comparableData.length})`);
      } else {
        console.log(`🎯 No comparables selected - analyzing all ${comparableData.length}`);
      }

      // Progress callback for real-time updates
      const handleProgress = (progressData) => {
        console.log('📊 Progress:', progressData);
        setAIProgress(progressData);

        // Update comparable scores in real-time as they are analyzed
        if (progressData.type === 'scored') {
          const { index, aiScore, photoSimilarityScore, dataBracketingScore, hasPhotos, reasoning } = progressData;

          // Map the result index back to the original comparable index
          const originalIndex = indexMapping[index];

          // Update the comparable data with score AND reasoning
          setComparableData(prevData => {
            const newData = [...prevData];
            if (originalIndex !== undefined && newData[originalIndex]) {
              console.log(`✅ Updating comparable ${originalIndex}: ${newData[originalIndex].address} with AI Score: ${aiScore}%`);
              newData[originalIndex] = {
                ...newData[originalIndex],
                aiScore,
                photoSimilarityScore,
                dataBracketingScore,
                hasPhotos,
                aiReasoning: reasoning || null,
                aiSelected: false
              };
            }
            return newData;
          });

          // Also update filtered data
          setFilteredComparableData(prevData => {
            const newData = [...prevData];
            if (originalIndex !== undefined && newData[originalIndex]) {
              newData[originalIndex] = {
                ...newData[originalIndex],
                aiScore,
                photoSimilarityScore,
                dataBracketingScore,
                hasPhotos,
                aiReasoning: reasoning || null,
                aiSelected: false
              };
            }
            return newData;
          });
        }
      };

      // Call AI selection service WITH STREAMING - pass only selected comparables
      // ✅ FIX: Analyze ALL comparables, not just 5
      // - User wants detailed analysis for all properties
      // - maxResults should match number of comparables to analyze
      const result = await apiService.getAISelectedComparablesStream(
        subjectProperty,
        comparablesToAnalyze,
        {
          maxResults: comparablesToAnalyze.length, // ✅ FIXED: Was hardcoded to 5, now analyzes ALL
          includeReasoning: true,
          uadCompliant: true,
          uploadedImages: selectedImages.length > 0 ? selectedImages : null // ✅ Pass uploaded images for priority
        },
        handleProgress,
        controller.signal
      );

      console.log('✅ AI Selection Result:', result);

      if (result.success && result.allScoredComparables) {
        // Use allScoredComparables to get scores for analyzed comparables
        const allScored = result.allScoredComparables;
        const topSelected = result.selectedComparables;

        // Update comparables with their AI scores using index mapping
        setComparableData(prevData => {
          const newData = [...prevData];
          allScored.forEach((scoredComp, i) => {
            const originalIndex = indexMapping[i];
            if (originalIndex !== undefined && newData[originalIndex]) {
              const isTopSelected = topSelected.find(
                sel => (sel.address || sel.StreetAddress) === (scoredComp.address || scoredComp.StreetAddress)
              );
              newData[originalIndex] = {
                ...newData[originalIndex],
                aiScore: scoredComp.aiScore,
                photoSimilarityScore: scoredComp.photoSimilarityScore,
                dataBracketingScore: scoredComp.dataBracketingScore,
                aiReasoning: isTopSelected?.reasoning || scoredComp.reasoning || null,
                aiConfidence: isTopSelected?.confidence || 0.5,
                aiSelected: !!isTopSelected,
                hasPhotos: scoredComp.hasPhotos
              };
            }
          });
          return newData;
        });

        // Auto-select the top AI-recommended comparables for export
        setSelectedComparables(topSelected);

        // Update filtered data with scores
        setFilteredComparableData(prevData => {
          const newData = [...prevData];
          allScored.forEach((scoredComp, i) => {
            const originalIndex = indexMapping[i];
            if (originalIndex !== undefined && newData[originalIndex]) {
              const isTopSelected = topSelected.find(
                sel => (sel.address || sel.StreetAddress) === (scoredComp.address || scoredComp.StreetAddress)
              );
              newData[originalIndex] = {
                ...newData[originalIndex],
                aiScore: scoredComp.aiScore,
                photoSimilarityScore: scoredComp.photoSimilarityScore,
                dataBracketingScore: scoredComp.dataBracketingScore,
                aiReasoning: isTopSelected?.reasoning || scoredComp.reasoning || null,
                aiConfidence: isTopSelected?.confidence || 0.5,
                aiSelected: !!isTopSelected,
                hasPhotos: scoredComp.hasPhotos
              };
            }
          });
          return newData;
        });

        setAISelectionResult(result);
        setAIProgress(null); // Clear progress

        // Show success message
        const analyzedCount = selectedForAI.length > 0 ? selectedForAI.length : comparableData.length;
        setMessages(prev => [...prev, {
          id: prev.length + 1,
          type: 'ai',
          content: `✅ AI analyzed ${analyzedCount} comparable${analyzedCount !== 1 ? 's' : ''} in ${result.processingTime}!\n\nTop match: ${topSelected[0]?.address || 'Unknown'} (${topSelected[0]?.aiScore}% score)\n\nScores and reasoning are now visible in the table!`,
          timestamp: new Date()
        }]);

      } else {
        setError('AI selection failed. Please try again.');
        setAIProgress(null);
      }

      setIsAISelecting(false);
      setAiAbortController(null); // ✅ FIX ISSUE #3: Clear abort controller

    } catch (error) {
      console.error('❌ AI Smart Select Error:', error);

      // ✅ FIX ISSUE #3: Handle user-initiated stop differently
      if (error.message === 'Analysis stopped by user') {
        setMessages(prev => [...prev, {
          id: prev.length + 1,
          type: 'ai',
          content: '🛑 AI analysis stopped by user',
          timestamp: new Date()
        }]);
      } else {
        setError(`AI selection failed: ${error.message}`);
      }

      setIsAISelecting(false);
      setAIProgress(null);
      setAiAbortController(null); // ✅ FIX ISSUE #3: Clear abort controller
    }
  };

  // ✅ FIX ISSUE #3: Stop AI analysis handler
  const handleStopAIAnalysis = () => {
    if (aiAbortController) {
      console.log('🛑 Stopping AI analysis...');
      aiAbortController.abort();
      setAiAbortController(null);
    }
  };

  // BRACKETING ANALYSIS HANDLER (NEW)
  const handleBracketingAnalysis = async () => {
    try {
      setIsBracketAnalyzing(true);
      setError(null);

      console.log('📊 Starting Bracketing Analysis...');

      // Validation
      if (!subjectProperty) {
        setError('Please select a subject property first');
        setIsBracketAnalyzing(false);
        return;
      }

      if (!comparableData || comparableData.length === 0) {
        setError('No comparables available. Please search for comparables first.');
        setIsBracketAnalyzing(false);
        return;
      }

      // Call bracketing analysis API
      const result = await apiService.analyzeBracketing(subjectProperty, comparableData);

      console.log('✅ Bracketing Analysis Result:', result);

      if (result.success) {
        setBracketingData(result);

        // Show success message
        const { overallBracketQuality } = result;
        setMessages(prev => [...prev, {
          id: prev.length + 1,
          type: 'ai',
          content: `📊 Bracketing analysis complete!\n\nOverall Quality: ${overallBracketQuality.rating} (${overallBracketQuality.percentage}%)\n${overallBracketQuality.greenFeatures} of ${overallBracketQuality.totalFeatures} features are well bracketed.\n\nBracketing indicators are now visible in the comparables table!`,
          timestamp: new Date()
        }]);
      } else {
        setError('Bracketing analysis failed. Please try again.');
      }

      setIsBracketAnalyzing(false);

    } catch (error) {
      console.error('❌ Bracketing Analysis Error:', error);
      setError(`Bracketing analysis failed: ${error.message}`);
      setIsBracketAnalyzing(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-900 text-white overflow-hidden">
      {/* Left Sidebar - Chat */}
      <div className="w-80 bg-gray-800 border-r border-gray-700 flex flex-col shrink-0">
        {/* Header */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                <span className="text-gray-900 font-bold">A</span>
              </div>
              <h1 className="text-xl font-semibold">Property AI</h1>
            </div>
            <button
              onClick={() => supabase.auth.signOut()}
              title="Sign out"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors text-xs"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Sign out
            </button>
          </div>
          <p className="text-gray-400 text-sm">
            Enter a property address to get started with market analysis and comparable properties.
          </p>
        </div>


        {/* Chat Messages - Simplified for Sidebar */}
        <div ref={leftChatRef} className="flex-1 overflow-y-auto p-2 space-y-2">
          {messages.map((message) => (
            <SimpleChatMessage
              key={message.id}
              message={message}
              isUser={message.type === 'user'}
            />
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-700 text-gray-300 p-2 rounded-lg flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span className="text-xs">Analyzing...</span>
              </div>
            </div>
          )}
        </div>

        {/* Chat Input */}
        <div className="p-4 border-t border-gray-700">

          {/* Address Input Row */}
          <div className="flex gap-2 mb-2">
            <AddressAutocomplete
              value={addressInput}
              onChange={setAddressInput}
              onSubmit={handleAddressInputSubmit}
              onSelectParsed={handleAddressSuggestionSelect}
              disabled={isLoading}
            />
            <button
              onClick={handleAddressInputSubmit}
              disabled={isLoading || !addressInput.trim()}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              Search
            </button>
          </div>

          {/* Chat/Query Input Row */}
          {selectedImages.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {selectedImages.map((image) => (
                <div key={image.id} className="relative">
                  <img
                    src={image.url}
                    alt={image.name}
                    className="w-16 h-16 object-cover rounded border border-gray-500"
                  />
                  <button
                    onClick={() => removeImage(image.id)}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-700"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs px-1 py-0.5 rounded-b truncate">
                    {image.name}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Ask a question or give a command..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                disabled={isLoading}
              />
            </div>

            {/* Image Upload Button */}
            <label className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors cursor-pointer flex items-center justify-center">
              <Paperclip className="w-4 h-4" />
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={isLoading}
              />
            </label>

            <button
              onClick={handleSendMessage}
              disabled={isLoading || (!inputMessage.trim() && selectedImages.length === 0)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Navigation Tabs */}
        <div className="bg-gray-800 border-b border-gray-700 p-4">
          <div className="flex space-x-1 overflow-x-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setCurrentView(item.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    currentView === item.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto p-6">
          {/* Existing views */}
          {currentView === 'chat' && (
            <div className="max-w-6xl mx-auto h-full flex flex-col">
              {/* Chat Messages in Main Area */}
              <div className="flex-1 overflow-y-auto space-y-4 p-4">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Send className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-semibold text-white mb-2">Property AI Assistant</h2>
                    <p className="text-gray-400 mb-6">
                      Enter a property address in the chat to begin your comprehensive market analysis.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
                      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                        <BarChart3 className="w-8 h-8 text-blue-400 mb-2" />
                        <h3 className="font-medium text-white mb-1">Market Analysis</h3>
                        <p className="text-sm text-gray-400">Get detailed market trends and conditions</p>
                      </div>
                      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                        <Home className="w-8 h-8 text-green-400 mb-2" />
                        <h3 className="font-medium text-white mb-1">Comparable Properties</h3>
                        <p className="text-sm text-gray-400">Find and analyze similar properties</p>
                      </div>
                      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                        <Camera className="w-8 h-8 text-purple-400 mb-2" />
                        <h3 className="font-medium text-white mb-1">Photo Analysis</h3>
                        <p className="text-sm text-gray-400">AI-powered property condition assessment</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  messages.map((message) => (
                    <ExpandableChatMessage
                      key={message.id}
                      message={message}
                      isUser={message.type === 'user'}
                    />
                  ))
                )}
                {isLoading && (
                  <div className="flex justify-center">
                    <div className="bg-gray-700 text-gray-300 p-3 rounded-lg flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Analyzing property...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {currentView === 'market-summary' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Market Summary</h2>
                <div className="text-sm text-gray-400">
                  {currentAddress ? `Market analysis for ${currentAddress}` : 'Enter address to view market data'}
                </div>
              </div>
              
              <PropertyAnalysis 
                subjectProperty={subjectProperty || propertyDetails} 
                debugData={debugData}
                currentAddress={currentAddress}
              />
            </div>
          )}

          {/* NEW: Property Edit View */}
          {currentView === 'property-edit' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Edit Subject Property</h2>
                <div className="text-sm text-gray-400">
                  {currentAddress ? `Editing ${currentAddress}` : 'Select a property to edit'}
                </div>
              </div>
              
              <EditablePropertyFields 
                propertyData={editedSubjectProperty || subjectProperty || propertyDetails}
                onPropertyUpdate={handlePropertyUpdate}
                currentAddress={currentAddress}
                isLoading={isLoading}
                onFindComparables={handleSubjectBasedSearch}
                onNavigateToComparables={() => setCurrentView('comparables')}
              />
            </div>
          )}

          {currentView === 'comparables' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">Comparable Properties</h2>
                  {comparableData.length > 0 && (
                    <div className="mt-1">
                      <p className={`text-lg font-medium ${filteredComparableData.length > 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                        {filteredComparableData.length > 0
                          ? `Showing ${filteredComparableData.length} comparable properties`
                          : `0 of ${comparableData.length} properties match filters`
                        }
                      </p>
                      {/* 🆕 NEW: Show total available info */}
                      {totalAvailable > 0 && totalAvailable > comparableData.length && (
                        <p className="text-sm text-gray-400 mt-1">
                          {showAllComparables
                            ? `Showing all ${totalAvailable} available (within ${lastFetchedRadius || 3} mi)`
                            : `${totalAvailable} total available within ${lastFetchedRadius || 3} mi radius`
                          }
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  {/* 🆕 NEW: Show All / Show Top toggle */}
                  {totalAvailable > displayLimit && (
                    <button
                      onClick={handleShowAllToggle}
                      className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                        showAllComparables
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {showAllComparables
                        ? `Show Top ${displayLimit}`
                        : `Show All ${totalAvailable}`
                      }
                    </button>
                  )}
                  <div className="text-sm text-gray-400">
                    {currentAddress ? `Properties similar to ${currentAddress}` : 'Edit property details first to find comparables'}
                  </div>
                </div>
              </div>
              
              {/* Instructions */}
              {!currentAddress && (
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 text-center">
                  <MapPin className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">Get Started</h3>
                  <p className="text-gray-400 mb-4">
                    1. Enter a property address in the chat<br/>
                    2. Go to "Edit Property" to set details and radius<br/>
                    3. Click "Find Comparables" to see results here
                  </p>
                </div>
              )}
              
              {/* Show "Ready to Find Comparables" only if we have NO comparables at all */}
              {currentAddress && comparableData.length === 0 && !isSearching && (
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 text-center">
                  <MapPin className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">Ready to Find Comparables</h3>
                  <p className="text-gray-400 mb-4">
                    Go to the "Edit Property" tab to set property details and search radius, then click "Find Comparables"
                  </p>
                  <button
                    onClick={() => setCurrentView('property-edit')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Go to Edit Property
                  </button>
                </div>
              )}

              {/* Filter Section - Show when we have comparables (even if all filtered out) */}
              {comparableData.length > 0 && (
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                  <h3 className="text-md font-medium text-white mb-4">Filter Comparable Properties</h3>
                  <PropertyFilters
                    onFiltersChange={handleFilterChange}
                    totalProperties={comparableData.length}
                    filteredCount={filteredComparableData.length}
                  />
                </div>
              )}

              {/* AI SMART SELECT BUTTON (Milestone 1) - WITH REAL-TIME PROGRESS */}
              {filteredComparableData.length > 0 && (
                <>
                  <div className="bg-gradient-to-r from-purple-900 to-blue-900 border border-purple-500 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                          🤖 AI Smart Select
                          <span className="text-xs bg-purple-600 px-2 py-1 rounded-full">LIVE</span>
                          {selectedForAI.length > 0 && (
                            <span className="text-xs bg-green-600 px-2 py-1 rounded-full">
                              {selectedForAI.length} selected
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-300">
                          {isAISelecting
                            ? 'Analyzing photos in real-time - scores & reasoning appear as each property is processed!'
                            : selectedForAI.length > 0
                              ? `Analyze ${selectedForAI.length} selected comparable${selectedForAI.length !== 1 ? 's' : ''} with AI photo comparison`
                              : `Select comparables below using the purple checkboxes, or analyze all ${filteredComparableData.length}`
                          }
                        </p>
                      </div>
                      {/* ✅ FIX ISSUE #3: Start/Stop buttons */}
                      <div className="flex gap-2">
                        {!isAISelecting ? (
                          <button
                            onClick={handleAISmartSelect}
                            disabled={!subjectProperty}
                            className="px-6 py-3 rounded-lg font-medium transition-all bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {selectedForAI.length > 0 ? (
                              `🚀 Analyze ${selectedForAI.length} Selected`
                            ) : (
                              `🚀 Analyze All ${filteredComparableData.length}`
                            )}
                          </button>
                        ) : (
                          <>
                            <button
                              disabled
                              className="px-6 py-3 rounded-lg font-medium bg-gray-600 cursor-not-allowed text-white"
                            >
                              <span className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Analyzing...
                              </span>
                            </button>
                            <button
                              onClick={handleStopAIAnalysis}
                              className="px-6 py-3 rounded-lg font-medium transition-all bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl"
                              title="Stop AI analysis"
                            >
                              🛑 Stop
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                  {/* REAL-TIME PROGRESS DISPLAY */}
                  {aiProgress && isAISelecting && (
                    <div className="mt-4 pt-4 border-t border-purple-400">
                      {/* Progress Bar */}
                      <div className="mb-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-300">Progress</span>
                          <span className="text-purple-300">{aiProgress.progress || 0}%</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${aiProgress.progress || 0}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Current Status */}
                      <div className="text-sm">
                        {aiProgress.type === 'subject_analysis' && (
                          <p className="text-yellow-400">📸 Analyzing subject property photos...</p>
                        )}
                        {aiProgress.type === 'subject_complete' && (
                          <p className="text-green-400">✅ Subject: {aiProgress.subjectDNA?.style} | {aiProgress.subjectDNA?.condition} condition</p>
                        )}
                        {aiProgress.type === 'analyzing' && (
                          <p className="text-blue-400">🔍 {aiProgress.message}</p>
                        )}
                        {aiProgress.type === 'scored' && (
                          <div className="bg-gray-800 rounded-lg p-3 mt-2">
                            <p className="text-green-400 font-medium">✅ {aiProgress.address}</p>
                            <div className="flex flex-wrap gap-3 mt-2 text-xs">
                              <span className={`px-2 py-1 rounded font-bold ${
                                aiProgress.aiScore >= 90 ? 'bg-purple-600' :
                                aiProgress.aiScore >= 80 ? 'bg-blue-600' :
                                aiProgress.aiScore >= 70 ? 'bg-green-600' :
                                aiProgress.aiScore >= 60 ? 'bg-yellow-600' : 'bg-gray-600'
                              } text-white`}>
                                🤖 {aiProgress.aiScore}%
                              </span>
                              <span className="px-2 py-1 bg-blue-800 text-blue-200 rounded">📷 {aiProgress.photoSimilarityScore}%</span>
                              <span className="px-2 py-1 bg-cyan-800 text-cyan-200 rounded">📊 {aiProgress.dataBracketingScore}%</span>
                              {!aiProgress.hasPhotos && <span className="px-2 py-1 bg-orange-800 text-orange-200 rounded">⚠️ No photos</span>}
                            </div>
                            {/* Show reasoning in real-time */}
                            {aiProgress.reasoning && (
                              <p className="mt-2 text-xs text-gray-300 bg-gray-700/50 p-2 rounded italic">
                                💬 {aiProgress.reasoning}
                              </p>
                            )}
                          </div>
                        )}
                        {aiProgress.type === 'generating_reasoning' && (
                          <p className="text-yellow-400">💭 Generating AI reasoning for top picks...</p>
                        )}
                      </div>

                      {/* Counter */}
                      {aiProgress.index !== undefined && (
                        <p className="text-xs text-gray-400 mt-2">
                          Analyzed {aiProgress.index + 1} of {aiProgress.total} properties
                        </p>
                      )}
                    </div>
                  )}

                    {/* Last Run Result */}
                    {aiSelectionResult && !isAISelecting && (
                      <div className="mt-3 pt-3 border-t border-purple-400">
                        <p className="text-sm text-green-400">
                          ✅ Last run: {aiSelectionResult.processingTime} |
                          Selected {aiSelectionResult.totalSelected} of {aiSelectionResult.totalAnalyzed} properties
                        </p>
                      </div>
                    )}
                  </div>

                  {/* BRACKETING ANALYSIS BUTTON - NEW */}
                  {(() => {
                    const saleStatusCount = filteredComparableData.filter(comp => {
                      const status = comp.mlsStatus || comp.rawData?.MlsStatus || comp.rawData?.StandardStatus || '';
                      return status.toLowerCase() === 'closed' || status.toLowerCase() === 'sold';
                    }).length;
                    const canShowBracketing = saleStatusCount >= 2;

                    return canShowBracketing ? (
                      <div className="bg-gradient-to-r from-green-900 to-cyan-900 border border-green-500 rounded-lg p-4 mt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                              📊 Bracketing Analysis
                              <span className="text-xs bg-green-600 px-2 py-1 rounded-full">
                                {saleStatusCount} Sale properties
                              </span>
                            </h3>
                            <p className="text-sm text-gray-300">
                              {isBracketAnalyzing
                                ? 'Analyzing how comparables bracket the subject property...'
                                : bracketingData
                                  ? `Analysis complete: ${bracketingData.overallBracketQuality.rating} quality (${bracketingData.overallBracketQuality.percentage}%)`
                                  : 'Analyze how comparables bracket the subject property on key features (GLA, lot size, age, beds, baths, garage)'
                              }
                            </p>
                          </div>
                          <button
                            onClick={handleBracketingAnalysis}
                            disabled={isBracketAnalyzing || !subjectProperty}
                            className={`px-6 py-3 rounded-lg font-medium transition-all ${
                              isBracketAnalyzing
                                ? 'bg-gray-600 cursor-not-allowed'
                                : 'bg-gradient-to-r from-green-600 to-cyan-600 hover:from-green-700 hover:to-cyan-700 text-white shadow-lg hover:shadow-xl'
                            }`}
                          >
                            {isBracketAnalyzing ? (
                              <span className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Analyzing...
                              </span>
                            ) : bracketingData ? (
                              '🔄 Re-analyze'
                            ) : (
                              '🚀 Run Bracketing Analysis'
                            )}
                          </button>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </>
              )}

              {/* Bracketing Dashboard Display - Inline on Comparables Page */}
              {bracketingData && (
                <div className="bg-gray-800 border border-green-500 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Bracketing Analysis Results</h3>
                    <button
                      onClick={() => setBracketingData(null)}
                      className="text-gray-400 hover:text-white text-sm"
                    >
                      ✕ Close
                    </button>
                  </div>
                  <BracketingDashboard bracketingData={bracketingData} />
                </div>
              )}

              {/* Message when filters remove all results */}
              {comparableData.length > 0 && filteredComparableData.length === 0 && (
                <div className="bg-yellow-900 border border-yellow-600 rounded-lg p-6 text-center">
                  <div className="text-4xl mb-3">🔍</div>
                  <h3 className="text-lg font-medium text-yellow-200 mb-2">No Properties Match Your Filters</h3>
                  <p className="text-yellow-300 mb-4">
                    Your current filters are too restrictive. {comparableData.length} properties are available but none match the selected criteria.
                  </p>
                  <p className="text-yellow-400 text-sm">
                    Try adjusting or clearing your filters above to see results.
                  </p>
                </div>
              )}

              {/* Results Table */}
              {filteredComparableData.length > 0 && (
                <div>
                  <ComparablesTable
                    comparableData={filteredComparableData}
                    currentAddress={currentAddress}
                    selectedComparables={selectedComparables}
                    onComparableSelection={handleComparableSelection}
                    selectedForAI={selectedForAI}
                    onAISelectionChange={handleAISelectionChange}
                    isAIAnalyzing={isAISelecting}
                    bracketingData={bracketingData}
                  />

                  {/* TOTAL EXPORT BUTTON (Milestone 2) */}
                  {selectedComparables.length > 0 && (
                    <div className="mt-4 bg-gray-800 border border-gray-700 rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <h3 className="text-md font-medium text-white mb-1">Export to TOTAL (AlaMode)</h3>
                        <p className="text-sm text-gray-400">
                          Download MISMO XML file with {selectedComparables.length} selected comparables
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await apiService.exportToTOTAL(subjectProperty, selectedComparables, {});
                            setMessages(prev => [...prev, {
                              id: prev.length + 1,
                              type: 'ai',
                              content: '✅ TOTAL export downloaded! Import the XML file in TOTAL software.',
                              timestamp: new Date()
                            }]);
                          } catch (error) {
                            setError(`Export failed: ${error.message}`);
                          }
                        }}
                        className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                      >
                        📄 Download MISMO XML
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Loading State */}
              {isSearching && (
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center">
                  <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">Searching for Comparables</h3>
                  <p className="text-gray-400">
                    Finding properties similar to your subject property...
                  </p>
                </div>
              )}
              
              {/* No Results Message */}
              {!isSearching && filteredComparableData.length === 0 && searchResults && searchResults.total === 0 && (
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center">
                  <MapPin className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No Comparable Properties Found</h3>
                  <p className="text-gray-400 mb-4">
                    Try adjusting your property details or expanding the search radius in the "Edit Property" tab.
                  </p>
                  <button
                    onClick={() => setCurrentView('property-edit')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Adjust Search Criteria
                  </button>
                </div>
              )}
            </div>
          )}

          {currentView === 'comparison' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Property Comparison</h2>
                <div className="text-sm text-gray-400">
                  {currentAddress 
                    ? `Comparing ${currentAddress} with ${selectedComparables.length} selected properties` 
                    : 'Enter address to compare properties'
                  }
                </div>
              </div>
              
              {selectedComparables.length === 0 ? (
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center">
                  <h3 className="text-lg font-medium text-white mb-2">No Properties Selected</h3>
                  <p className="text-gray-400 mb-4">
                    Go to the Comparables tab and check the properties you want to compare side-by-side.
                  </p>
                  <button
                    onClick={() => setCurrentView('comparables')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Go to Comparables
                  </button>
                </div>
              ) : (
                <PropertyComparisonTable 
                  subjectProperty={subjectProperty}
                  comparableData={selectedComparables} 
                  currentAddress={currentAddress} 
                />
              )}
            </div>
          )}


          {/* UPDATED: Adjustments View */}
          {currentView === 'adjustments' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Manual Adjustments</h2>
                <div className="text-sm text-gray-400">
                  {currentAddress ? `Adjustments for ${currentAddress}` : 'Enter address to make adjustments'}
                </div>
              </div>

              {filteredComparableData.length > 0 ? (
                <ManualAdjustments 
                  comparableData={filteredComparableData}
                  onAdjustmentsChange={handleAdjustmentsChange}
                />
              ) : (
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 text-center">
                  <Calculator className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg mb-2">No Comparable Properties Available</p>
                  <p className="text-gray-500 text-sm">
                    Please search for a property first to access the manual adjustments feature.
                  </p>
                </div>
              )}

              {/* Show adjusted results if available */}
              {adjustedComparables.length > 0 && (
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-white mb-4">Adjustment Results Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-700 p-4 rounded-lg text-center">
                      <div className="text-lg font-bold text-blue-400">
                        {adjustedComparables.length}
                      </div>
                      <div className="text-sm text-gray-300">Properties Adjusted</div>
                    </div>
                    <div className="bg-gray-700 p-4 rounded-lg text-center">
                      <div className="text-lg font-bold text-green-400">
                        {adjustedComparables.filter(c => c.totalAdjustment > 0).length}
                      </div>
                      <div className="text-sm text-gray-300">Positive Adjustments</div>
                    </div>
                    <div className="bg-gray-700 p-4 rounded-lg text-center">
                      <div className="text-lg font-bold text-red-400">
                        {adjustedComparables.filter(c => c.totalAdjustment < 0).length}
                      </div>
                      <div className="text-sm text-gray-300">Negative Adjustments</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* NEW: Smart Adjustments View */}
          {currentView === 'smart-adjustments' && (
            <div className="space-y-6">
              <SmartAdjustments 
                comparableData={filteredComparableData}
                subjectProperty={subjectProperty}
                onAdjustmentsChange={(adjustments) => {
                  console.log('🔧 Smart adjustments applied:', adjustments);
                  setAdjustedComparables(adjustments);
                }}
              />
            </div>
          )}

          {/* NEW: AI Narrative View - NOW WITH URAR FORM */}
          {currentView === 'narrative' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">URAR Form 1004 - Uniform Residential Appraisal Report</h2>
                <div className="text-sm text-gray-400">
                  {currentAddress ? `Appraisal report for ${currentAddress}` : 'Enter address to begin'}
                </div>
              </div>

              <UADReportView
                subjectProperty={subjectProperty || propertyDetails}
                selectedComparables={selectedComparables}
                marketData={marketData}
                currentAddress={currentAddress}
                uploadedImages={selectedImages}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyAI;